import { Dialog, FileUpload, RadioGroup } from "@ark-ui/solid";
import { IconButton, TextButton } from "../button/Button";
import { isEmpty, isNil, omit } from "lodash-es";
import { Icon } from "../icon/Icon";
import type { VoidComponent } from "solid-js";
import { createEffect, createResource, createSignal, For, Match, Show, Switch, untrack } from "solid-js";
import GlobalTabs from "../tabs/GlobalTabs";
import type {
  CustomerResource,
  CustomerResourcePage,
  PendingCustomerResource,
} from "../../services/CustomerResourceService";
import { PAGE_SIZE } from "../../services/CustomerResourceService";
import { useTranslation } from "../../state/i18n";
import { MoreAppError } from "../../MoreAppError";
import ButtonLink from "../link/ButtonLink";
import InlineMessage from "../inlineMessage/InlineMessage";
import { MAX_RESOURCES_LIMIT, USER_INPUT_DEBOUNCE } from "../../constants";
import Radio from "../radio/Radio";
import Pagination from "../pagination/Pagination";
import { DestructiveModal } from "./Modal";
import SearchBar from "../searchBar/SearchBar";
import { debounce } from "@solid-primitives/scheduled";
import { type FileUploadError, toCustomerFile, translateFileUploadError } from "../../utils/uploadUtil";
import Text from "../text/Text";
import logger from "../../utils/logger";
import EmptyState from "../emptyState/EmptyState";
import ProgressBar from "../progressBar/ProgressBar";
import { useModalManager } from "./ModalManager";

const MAX_FILE_SIZE = 1024 * 1024 * 5; // 5MiB

type UploadStatus = "pending" | "uploading" | "uploaded" | "error";
export type MediaUploadProps = {
  onFileSelected: (file: PendingCustomerResource) => Promise<CustomerResource>;
  status: UploadStatus;
  setStatus: (status: UploadStatus) => void;
  selectedFile?: CustomerResource;
  setSelectedFile: (resource: CustomerResource | undefined) => void;
  uploadingFile?: PendingCustomerResource;
  setUploadingFile: (uploadingFile: PendingCustomerResource | undefined) => void;
  uploadedFile?: CustomerResource;
  setUploadedFile: (uploadingFile: CustomerResource | undefined) => void;
  onDelete: (file: CustomerResource) => Promise<void>;
  onCancelUpload: () => void;
  progress: number;
  setProgress: (value: number) => void;
  page?: CustomerResourcePage;
};

type MediaLibraryProps = {
  selectedFile?: CustomerResource;
  setSelectedFile: (resource: CustomerResource | undefined) => void;
  uploadStatus: UploadStatus;
  onCancelUpload: () => void;
  onDelete: (value: CustomerResource) => Promise<void>;
  progress: number;
  page?: CustomerResourcePage;
  query: string;
  onQueryChange: (query: string) => void;
};

type MediaModalTabs = "upload" | "library";
export type MediaModalProps = {
  onSelect: (file: CustomerResource) => void;
  progress: number;
  setProgress: (value: number) => void;
  onDelete: (value: CustomerResource) => Promise<void>;
  mediaUpload: Pick<MediaUploadProps, "onFileSelected">;
  fetchPage: (page: number, query?: string) => Promise<CustomerResourcePage>;
  onCancel: () => void;
  defaultTab?: MediaModalTabs;
};

const MediaModal = (props: MediaModalProps) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const [tabKey, setTabKey] = createSignal<MediaModalTabs>(props.defaultTab ?? "upload");
  const [uploadStatus, setUploadStatus] = createSignal<UploadStatus>("pending");
  const [uploadingFile, setUploadingFile] = createSignal<PendingCustomerResource>();
  const [uploadedFile, setUploadedFile] = createSignal<CustomerResource>();
  const [selectedFile, setSelectedFile] = createSignal<CustomerResource>();
  const [page, setPage] = createSignal(1);
  const [inputQuery, setInputQuery] = createSignal("");
  const [searchQuery, setSearchQuery] = createSignal("");
  // oxlint-disable-next-line solid/reactivity -- False-positive, 'debounce' retains reactivity
  const debouncedSearchQuery = debounce((query: string) => {
    if (query !== searchQuery()) {
      setSearchQuery(query);
      setPage(1); // Reset to the first page
    }
  }, USER_INPUT_DEBOUNCE);

  const onQueryChange = (query: string) => {
    setInputQuery(query);
    debouncedSearchQuery(query);
  };

  const [imagePage, { refetch }] = createResource(
    () => ({ page: page(), query: searchQuery() }),
    async ({ page, query }) => await untrack(() => props.fetchPage)(page - 1, query),
  );

  createEffect(() => {
    if (uploadStatus() === "uploaded") {
      refetch();
    }
  });

  const reset = () => {
    props.setProgress(0);
    setSelectedFile(undefined);
    setUploadStatus("pending");
  };

  const onDelete = async (resource: CustomerResource) =>
    open({
      keepParent: true,
      content: () => (
        <DestructiveModal
          cancelLabel={t(($) => $.CANCEL_BUTTON)}
          confirmLabel={t(($) => $.DELETE_BUTTON)}
          title={t(($) => $.MEDIA_MODAL_IMAGE_DELETE_TITLE)}
          description={t(($) => $.MEDIA_MODAL_IMAGE_DELETE_DESCRIPTION, { name: resource.name })}
          onConfirm={async () => {
            await props.onDelete(resource);
            if (resource.id === uploadedFile()?.id) {
              reset();
            } else if (resource.id === selectedFile()?.id) {
              setSelectedFile(undefined);
            }
            refetch();
          }}
        />
      ),
    });

  const onCancel = () => {
    reset();
    props.onCancel();
  };

  return (
    <div class="flex size-161 flex-col gap-4 p-6 shadow-elevation-light-5">
      <div class="flex size-full min-h-0 flex-col gap-2">
        <Dialog.Title class="flex items-center gap-2">
          <div class="flex size-9 flex-none shrink-0 items-center justify-center rounded-full bg-surface-status-subtle-informative">
            <Icon name="uploadAlt" size="xl" type="solid" class="icon-status-informative" />
          </div>
          <Text text={t(($) => $.MEDIA_MODAL_IMAGE_TITLE)} truncate={2} class="flex text-header-lg text-primary" />
        </Dialog.Title>
        <Dialog.Description class="h-full min-h-0">
          <GlobalTabs
            initialTab={tabKey()}
            onTabChange={setTabKey}
            items={[
              {
                label: () => t(($) => $.MEDIA_MODAL_IMAGE_UPLOAD_FILE_TAB),
                key: "upload",
                classes: "mt-4",
                Slot: () => (
                  <MediaUpload
                    {...props.mediaUpload}
                    status={uploadStatus()}
                    setStatus={setUploadStatus}
                    selectedFile={selectedFile()}
                    setSelectedFile={setSelectedFile}
                    uploadingFile={uploadingFile()}
                    setUploadingFile={setUploadingFile}
                    uploadedFile={uploadedFile()}
                    setUploadedFile={setUploadedFile}
                    onCancelUpload={onCancel}
                    progress={props.progress}
                    setProgress={props.setProgress}
                    onDelete={onDelete}
                    page={imagePage()}
                  />
                ),
              },
              {
                label: () => t(($) => $.MEDIA_MODAL_IMAGE_LIBRARY_TAB),
                key: "library",
                classes: "mt-2",
                Slot: () => (
                  <MediaLibrary
                    selectedFile={selectedFile()}
                    setSelectedFile={setSelectedFile}
                    page={imagePage()}
                    uploadStatus={uploadStatus()}
                    onCancelUpload={onCancel}
                    progress={props.progress}
                    onDelete={onDelete}
                    query={inputQuery()}
                    onQueryChange={onQueryChange}
                  />
                ),
              },
            ]}
          />
        </Dialog.Description>
      </div>
      <div class="mt-1 flex flex-row">
        <Show when={tabKey() === "library"}>
          <Pagination
            initialPage={page()}
            totalElements={imagePage()?.totalElements ?? 0}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
          />
        </Show>
        <div class="flex min-h-0 w-full justify-end gap-2">
          <Dialog.CloseTrigger
            onClick={() => {
              reset();
            }}
            asChild={(parentProps) => (
              <TextButton
                {...omit(parentProps(), "children")}
                text={t(($) => $.CANCEL_BUTTON)}
                variant="secondary"
                size="large"
              />
            )}
          />
          <Dialog.Context>
            {(context) => (
              <TextButton
                text={t(($) => $.SELECT_BUTTON)}
                disabled={isNil(selectedFile())}
                variant="primary"
                size="large"
                onClick={() => {
                  const file = selectedFile();
                  if (!isNil(file)) {
                    props.onSelect(file);
                  }
                  context().setOpen(false);
                  reset();
                }}
              />
            )}
          </Dialog.Context>
        </div>
      </div>
    </div>
  );
};

const MediaLibrary: VoidComponent<MediaLibraryProps> = (props: MediaLibraryProps) => {
  const { t } = useTranslation();
  return (
    <>
      <SearchBar
        query={props.query}
        onQueryChange={props.onQueryChange}
        placeholder={t(($) => $.MEDIA_MODAL_IMAGE_LIBRARY_SEARCH_PLACEHOLDER)}
      />
      <Show
        when={!isEmpty(props.page?.content)}
        fallback={
          <EmptyState
            title={t(($) => $.MEDIA_MODAL_IMAGE_LIBRARY_EMPTY_STATE_TITLE)}
            description={t(($) => $.MEDIA_MODAL_IMAGE_LIBRARY_EMPTY_STATE_DESCRIPTION)}
          />
        }
      >
        <RadioGroup.Root
          class="mt-2.5"
          orientation="horizontal"
          value={props.selectedFile?.id}
          onValueChange={(details) => {
            const customerResource = props.page?.content?.find((resource) => resource.id === details.value);
            if (!isNil(customerResource)) {
              props.setSelectedFile(customerResource);
            }
          }}
        >
          <div class="grid grid-cols-3 gap-4">
            <For each={props.page?.content}>
              {(resource) => (
                <RadioGroup.Item
                  value={resource.id}
                  // oxlint-disable-next-line better-tailwindcss/no-restricted-classes
                  class="group relative flex size-47 max-w-47 flex-col overflow-hidden rounded-sm border border-subtle has-[input:focus-visible]:outline-default"
                >
                  <RadioGroup.ItemHiddenInput />
                  <div class="relative flex h-33 items-center justify-center border-b border-subtle bg-surface-extra-subtle group-checked:bg-surface-active-brand">
                    <div class="absolute top-2 right-2">
                      <IconButton
                        variant="secondary"
                        icon="trash"
                        size="medium"
                        onClick={() => props.onDelete(resource)}
                        aria-label={t(($) => $.DELETE_BUTTON)}
                      />
                    </div>
                    <img
                      class="size-full min-h-0 object-contain"
                      src={resource.sourceThumbnail ?? resource.source}
                      alt={resource.name}
                    />
                  </div>
                  <div class="flex min-w-0 grow gap-1 bg-surface-default p-2">
                    <div class="mt-1">
                      <RadioGroup.ItemControl asChild={(itemProps) => <Radio {...itemProps} />} />
                    </div>
                    <div class="flex w-full flex-col">
                      <RadioGroup.ItemText
                        asChild={() => (
                          <Text class="mr-4 text-base-medium text-primary" text={resource.name} truncate />
                        )}
                      />
                      <div class="text-small-medium text-secondary uppercase">
                        <span>{resource.extension}</span>
                        <span> • </span>
                        <span>{resource.fileSize}</span>
                      </div>
                    </div>
                  </div>
                </RadioGroup.Item>
              )}
            </For>
          </div>
        </RadioGroup.Root>
      </Show>
    </>
  );
};

const MediaUpload: VoidComponent<MediaUploadProps> = (props: MediaUploadProps) => {
  const { t } = useTranslation();
  const [active, setActive] = createSignal(false);
  const [uploadError, setUploadError] = createSignal<FileUploadError>();

  const resetUpload = () => {
    props.setUploadingFile(undefined);
    props.setProgress(0);
  };

  const setError = (uploadError: FileUploadError) => {
    props.setStatus("error");
    setUploadError(uploadError);
  };

  const uploadFile = async (files: File[]) => {
    const uploadFile = files[0];
    if (isNil(uploadFile)) {
      return;
    }
    const customerFile = toCustomerFile(uploadFile);
    props.setUploadingFile(customerFile);
    props.setStatus("uploading");
    try {
      const resource = await props.onFileSelected(customerFile);
      props.setSelectedFile(resource);
      props.setUploadedFile(resource);
      props.setStatus("uploaded");
    } catch (e) {
      if (e instanceof MoreAppError && e.code === "REQUEST_ABORTED") {
        setError("UPLOAD_ABORTED");
      } else {
        setError("UPLOAD_ERROR");
        logger.error("Failed to upload file", e);
      }
    } finally {
      resetUpload();
    }
  };

  const uploadMedia = () => (
    <FileUpload.Root
      class="relative flex size-full items-center justify-center rounded-sm border border-dashed border-subtle"
      classList={{ "bg-surface-hover": active() }}
      maxFiles={1}
      maxFileSize={MAX_FILE_SIZE}
      accept="image/*,application/pdf"
      onFileAccept={(details) => {
        void uploadFile(details.files);
      }}
      onFileChange={() => {
        setActive(false);
      }}
      onFileReject={(details) => {
        if (!isEmpty(details.files) && !isEmpty(details.files[0].errors)) {
          // Only one error can be shown, so pick the first one
          setError(details.files[0].errors[0]);
        }
      }}
    >
      <FileUpload.Dropzone
        class="flex size-full items-center justify-center rounded-sm outline-offset-2 focus-visible:outline-default"
        onDragOver={() => {
          setActive(true);
        }}
        onDragLeave={() => {
          setActive(false);
        }}
      >
        <div class="flex w-68 flex-col items-center">
          <Switch>
            <Match when={props.status === "error"}>
              <div class="flex size-12 flex-none shrink-0 items-center justify-center rounded-full bg-surface-destructive">
                <Icon name="exclamationCircle" size="xl" type="solid" class="icon-status-error" />
              </div>
              <FileUpload.Label class="mt-2 text-center text-small-medium text-primary">
                <h3 class="w-60 text-base-medium text-status-error">
                  {translateFileUploadError(uploadError()!, MAX_FILE_SIZE)}
                </h3>
                <p>
                  <span>{t(($) => $.MEDIA_MODAL_IMAGE_UPLOAD_FILE_DROP_AFTER_ERROR)}</span>
                  <FileUpload.Trigger
                    asChild={(parentProps) => (
                      <ButtonLink
                        label={t(($) => $.MEDIA_MODAL_IMAGE_UPLOAD_FILE_SELECT)}
                        tabIndex={-1}
                        {...omit(parentProps(), "children")}
                      />
                    )}
                  />
                </p>
              </FileUpload.Label>
            </Match>
            <Match when={props.status === "pending"}>
              <div class="flex size-12 flex-none shrink-0 items-center justify-center rounded-full bg-surface-status-subtle-informative">
                <Icon name="uploadAlt" size="xl" type="outline" class="icon-status-informative" />
              </div>
              <FileUpload.Label class="mt-3 text-base-medium text-secondary">
                <span>{t(($) => $.MEDIA_MODAL_IMAGE_UPLOAD_FILE_DROP)}</span>
                <FileUpload.Trigger
                  asChild={(parentProps) => (
                    <ButtonLink
                      label={t(($) => $.MEDIA_MODAL_IMAGE_UPLOAD_FILE_SELECT)}
                      tabIndex={-1}
                      {...omit(parentProps(), "children")}
                    />
                  )}
                />
              </FileUpload.Label>
              <p class="max-w-70 text-center text-small-regular text-secondary">
                {t(($) => $.MEDIA_MODAL_IMAGE_UPLOAD_FILE_SUPPORT)}
              </p>
            </Match>
          </Switch>
        </div>
      </FileUpload.Dropzone>
      <FileUpload.HiddenInput data-testid="fileInput" />
    </FileUpload.Root>
  );

  const uploadingMedia = () => (
    <div class="size-full rounded-sm border border-dashed border-subtle">
      <div class="relative flex size-full flex-col items-center justify-center">
        <div class="absolute top-2 right-2">
          <IconButton
            icon="x"
            aria-label={t(($) => $.CANCEL_BUTTON)}
            size="medium"
            variant="secondary"
            onClick={props.onCancelUpload}
          />
        </div>
        <div class="flex size-12 flex-none shrink-0 items-center justify-center rounded-full bg-surface-status-subtle-informative">
          <Icon name="uploadAlt" size="xl" type="outline" class="icon-status-informative" />
        </div>
        <div class="mt-2 w-71.5">
          <ProgressBar
            status="informative"
            variant="determinate"
            label={props.uploadingFile?.fileName ?? ""}
            current={props.progress}
            max={100}
            backgroundColor="tertiary"
          />
        </div>
      </div>
    </div>
  );

  const fileLimitError = () => (
    <div class="size-full rounded-sm border border-dashed border-subtle">
      <div class="flex size-full items-center justify-center">
        <div class="flex size-full w-70 flex-col items-center justify-center gap-2 overflow-hidden">
          <div class="flex size-12 flex-none shrink-0 items-center justify-center rounded-full bg-surface-status-subtle-warning">
            <Icon name="outlineExclamation" size="xl" type="solid" class="icon-status-warning" />
          </div>
          <h3 class="text-header-lg text-primary">{t(($) => $.MEDIA_MODAL_IMAGE_LIMIT_TITLE)}</h3>
          <p class="text-base-regular text-secondary">{t(($) => $.MEDIA_MODAL_IMAGE_LIMIT_DESCRIPTION)}</p>
          <div class="">
            <InlineMessage
              variant="warning"
              label={t(($) => $.MEDIA_MODAL_IMAGE_LIMIT_INLINE, { count: MAX_RESOURCES_LIMIT })}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const uploadedMedia = () => (
    <div class="flex size-full min-h-0 flex-col gap-4">
      <div class="h-full min-h-0 rounded-md border border-subtle">
        <div class="relative h-full min-h-0">
          <div class="absolute top-2 right-2">
            <IconButton
              variant="secondary"
              icon="trash"
              size="medium"
              onClick={() => props.onDelete(props.uploadedFile!)}
              aria-label={t(($) => $.DELETE_BUTTON)}
            />
          </div>
          <img
            class="size-full min-h-0 object-contain"
            src={props.uploadedFile?.source}
            alt={props.uploadedFile?.name}
          />
        </div>
      </div>

      <div class="flex w-full justify-start">
        <div class="w-full">
          <Text as="p" class="text-base-medium text-primary" text={props.uploadedFile?.name ?? ""} truncate />
          <div class="text-small-medium text-secondary uppercase">
            <span>{props.uploadedFile?.extension}</span>
            <span> • </span>
            <span>{props.uploadedFile?.fileSize}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Switch>
      <Match when={props.status !== "uploaded" && (props.page?.totalElements ?? 0) >= MAX_RESOURCES_LIMIT}>
        {fileLimitError()}
      </Match>
      <Match when={props.status === "pending" || props.status === "error"}>{uploadMedia()}</Match>
      <Match when={props.status === "uploading"}>{uploadingMedia()}</Match>
      <Match when={props.status === "uploaded"}>{uploadedMedia()}</Match>
    </Switch>
  );
};

export default MediaModal;
